import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Video, Calendar, Clock, Users, ArrowLeft, ExternalLink, Play, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const OnlineClassList = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    subject_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    meeting_link: '',
    meeting_platform: 'jitsi'  // Default to Jitsi (free)
  });

  useEffect(() => {
    fetchData();
  }, [filterCourse]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classesRes, coursesRes] = await Promise.all([
        api.get('/lms/online-classes', { 
          params: { course_id: filterCourse !== 'all' ? filterCourse : undefined } 
        }),
        api.get('/lms/teacher/courses')
      ]);
      
      setClasses(classesRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load online classes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // meeting_link is now optional - will be auto-generated
    if (!formData.title || !formData.course_id || !formData.scheduled_date || !formData.scheduled_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingClass) {
        await api.put(`/lms/online-classes/${editingClass.id}`, formData);
        toast.success('Online class updated successfully');
      } else {
        const response = await api.post('/lms/online-classes', formData);
        toast.success(`Online class scheduled! Meeting link: ${response.data.meeting_link || 'Generated'}`);
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving class:', error);
      toast.error(error.response?.data?.detail || 'Failed to save online class');
    }
  };

  const handleStatusChange = async (classId, newStatus) => {
    try {
      await api.put(`/lms/online-classes/${classId}/status?status=${newStatus}`);
      toast.success(`Class ${newStatus === 'live' ? 'started' : newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this class?')) return;
    
    try {
      await api.delete(`/lms/online-classes/${id}`);
      toast.success('Online class cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel class');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      subject_id: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      meeting_link: '',
      meeting_platform: 'google_meet'
    });
    setEditingClass(null);
  };

  const openEditModal = (item) => {
    setEditingClass(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      course_id: item.course_id || '',
      subject_id: item.subject_id || '',
      scheduled_date: item.scheduled_date || '',
      scheduled_time: item.scheduled_time || '',
      duration_minutes: item.duration_minutes || 60,
      meeting_link: item.meeting_link || '',
      meeting_platform: item.meeting_platform || 'google_meet'
    });
    setIsModalOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full animate-pulse"><span className="w-2 h-2 bg-red-500 rounded-full"></span> LIVE</span>;
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full"><CheckCircle size={12} /> Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">Cancelled</span>;
      default:
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Scheduled</span>;
    }
  };

  const getPlatformIcon = (platform) => {
    return platform === 'zoom' ? '📹' : '🎥';
  };

  return (
    <div className="space-y-6" data-testid="lms-online-class-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Online Classes</h1>
            <p className="text-gray-600">Schedule and manage live video classes</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-red-600 hover:bg-red-700"
          data-testid="schedule-class-btn"
        >
          <Plus size={18} className="mr-2" /> Schedule Class
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <Select value={filterCourse} onValueChange={setFilterCourse}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Filter by Course" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map(course => (
                <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Classes List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : classes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No online classes scheduled yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {classes.map((item) => (
            <Card key={item.id} className={`${item.status === 'live' ? 'border-red-300 bg-red-50' : ''}`} data-testid={`class-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${item.status === 'live' ? 'bg-red-100' : 'bg-blue-100'}`}>
                      <Video className={`${item.status === 'live' ? 'text-red-600' : 'text-blue-600'}`} size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800">{item.title}</h3>
                        {getStatusBadge(item.status)}
                      </div>
                      <p className="text-sm text-gray-500">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(item.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {item.scheduled_time} ({item.duration_minutes} min)
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {item.attendees_count || 0} joined
                        </span>
                        <span>{getPlatformIcon(item.meeting_platform)} {item.meeting_platform === 'jitsi' ? 'Jitsi Meet' : item.meeting_platform === 'zoom' ? 'Zoom' : 'Google Meet'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status === 'scheduled' && (
                      <Button 
                        onClick={() => handleStatusChange(item.id, 'live')}
                        className="bg-red-600 hover:bg-red-700"
                        size="sm"
                      >
                        <Play size={16} className="mr-1" /> Start Class
                      </Button>
                    )}
                    {item.status === 'live' && (
                      <>
                        <Button 
                          onClick={() => window.open(item.meeting_link, '_blank')}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          <ExternalLink size={16} className="mr-1" /> Join
                        </Button>
                        <Button 
                          onClick={() => handleStatusChange(item.id, 'completed')}
                          variant="outline"
                          size="sm"
                        >
                          End Class
                        </Button>
                      </>
                    )}
                    {item.status === 'scheduled' && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openEditModal(item)}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Schedule Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingClass ? 'Edit Online Class' : 'Schedule Online Class'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Introduction to Data Structures"
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the class"
                rows={2}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Course *</label>
              <Select 
                value={formData.course_id} 
                onValueChange={(val) => setFormData({...formData, course_id: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Date *</label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Time *</label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Duration (minutes)</label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 60})}
                  min={15}
                  max={180}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Platform</label>
                <Select 
                  value={formData.meeting_platform} 
                  onValueChange={(val) => setFormData({...formData, meeting_platform: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jitsi">Jitsi Meet (Free - Auto Generate)</SelectItem>
                    <SelectItem value="google_meet">Google Meet</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Meeting Link {formData.meeting_platform !== 'jitsi' && '*'}</label>
              <Input
                value={formData.meeting_link}
                onChange={(e) => setFormData({...formData, meeting_link: e.target.value})}
                placeholder={formData.meeting_platform === 'jitsi' ? 'Leave empty to auto-generate' : 'https://meet.google.com/xxx-xxxx-xxx'}
                disabled={formData.meeting_platform === 'jitsi' && !formData.meeting_link}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.meeting_platform === 'jitsi' 
                  ? '🎉 A free Jitsi Meet link will be auto-generated - no account needed!'
                  : 'Paste your Google Meet or Zoom meeting link here'}
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                {editingClass ? 'Update' : 'Schedule'} Class
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnlineClassList;
